import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Carries the free bonus entries from onecountry.com over to this store.
 *
 * onecountry.com's /pages/discount-redirect adds its own bonus variant to that
 * store's cart and sets an `oc_bonus` session cookie on `.onecountry.com`
 * (ocshopify-compiled templates/page.redirects.liquid). The two stores have
 * separate carts, so while that cookie exists this module adds the host bonus
 * variant here, but only once the cart holds something else.
 * oc-membership-guard.liquid removes the bonus again if it is ever left alone.
 *
 * The cookie is deleted once the shopper reaches the cart (page or drawer) with
 * the bonus already in it, or starts checkout. The line stays in this cart, but
 * a later session won't add it again. Clearing on cart view only when the bonus
 * is present means peeking at an empty drawer doesn't throw the offer away.
 *
 * Mutations dispatch the theme's CartLinesUpdateEvent with the response's
 * section HTML, so the drawer and cart count re-render exactly as they do for a
 * normal add. `action: 'update'` rather than 'add' keeps the drawer from
 * auto-opening on page load.
 */

const COOKIE_NAME = 'oc_bonus';
const SOURCE = 'oc-bonus-carry';

const config = document.querySelector('script[data-oc-bonus-variant-id]');
const bonusVariantId = Number(config?.getAttribute('data-oc-bonus-variant-id'));

function hasBonusCookie() {
  return document.cookie.split(';').some((cookie) => cookie.trim().startsWith(`${COOKIE_NAME}=`));
}

function clearBonusCookie() {
  // Must repeat the domain it was set with, or the browser keeps the cookie.
  document.cookie = `${COOKIE_NAME}=; domain=.onecountry.com; path=/; Max-Age=0; SameSite=Lax; Secure`;
  document.cookie = `${COOKIE_NAME}=; path=/; Max-Age=0`;
}

function isViewingCart() {
  const cartPath = Theme.routes.cart_url || '/cart';
  if (window.location.pathname === cartPath) return true;
  return Boolean(document.querySelector('theme-drawer#cart-drawer')?.hasAttribute('open'));
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * POSTs a cart mutation with the cart sections attached, then announces it so
 * the theme re-renders.
 * @param {string} url
 * @param {object} body
 * @param {(response: any) => string} lineKey - Pulls the bonus line's key from the response.
 */
async function mutateCart(url, body, lineKey) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
    .filter(Boolean);

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: sectionIds.join(',') }),
  });
  const json = await response.json();
  if (!response.ok || json.status) throw new Error(json.description || json.message || 'Cart update failed');

  const cart = await fetchCart();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: lineKey(json), quantity: 1 }],
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: json.sections, source: SOURCE, didError: false },
      }),
    })
  );
}

async function syncBonus() {
  const cart = await fetchCart();
  const bonusLine = cart.items.find((item) => item.variant_id === bonusVariantId);

  // One bonus per order, however the quantity got raised. Runs even after the
  // cookie is cleared, since that happens on cart view — before the shopper
  // could otherwise bump the quantity freely.
  if (bonusLine && bonusLine.quantity > 1) {
    await mutateCart(
      `${Theme.routes.cart_change_url}.js`,
      { id: bonusLine.key, quantity: 1 },
      () => bonusLine.key
    );
  }

  if (!hasBonusCookie()) return;

  if (bonusLine) {
    if (isViewingCart()) clearBonusCookie();
    return;
  }

  // Never alone: wait until the shopper has added something of their own.
  if (!cart.items.some((item) => item.variant_id !== bonusVariantId)) return;

  await mutateCart(
    Theme.routes.cart_add_url,
    { items: [{ id: bonusVariantId, quantity: 1 }] },
    (response) => response.items?.[0]?.key
  );
  if (isViewingCart()) clearBonusCookie();
}

// Serialises runs: a trigger that arrives mid-sync schedules one more pass
// instead of being dropped or racing the in-flight add.
let running = false;
let rerun = false;
async function requestSync() {
  if (running) {
    rerun = true;
    return;
  }
  running = true;
  try {
    do {
      rerun = false;
      await syncBonus();
    } while (rerun);
  } catch (error) {
    console.warn('[oc-bonus-carry]', error);
  } finally {
    running = false;
  }
}

if (Number.isInteger(bonusVariantId) && bonusVariantId > 0) {
  requestSync();

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(({ detail } = {}) => {
        if (detail?.source !== SOURCE && !detail?.didError) requestSync();
      })
      .catch(() => {});
  });

  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target instanceof Element && event.target.id === 'cart-drawer') requestSync();
  });

  // Checkout ends the session's claim whatever the cart holds. Capture phase so
  // it runs before the form navigates away.
  document.addEventListener(
    'click',
    (event) => {
      if (event.target instanceof Element && event.target.closest('[name="checkout"]')) clearBonusCookie();
    },
    true
  );
}
